import { FC, useState } from 'react';

import { Button, Flex, Select, Spin, Typography } from 'antd';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { useTheme } from 'styled-components';
import useSWR from 'swr';

import IconDownload from '@/assets/icons/icon-download.svg?react';
import IconError from '@/assets/icons/icon-error.svg?react';
import IconInfo from '@/assets/icons/icon-info.svg?react';
import Card from '@/components/RCard';
import RToggleButtons from '@/components/RToggleButtons';
import Tooltip from '@/components/RTooltip';
import { SimulationResult } from '@/types/simulations';

import PNLStorageTable from './PNLStorageTable';
import PNLWithoutStorageTable from './PNLWithoutStorageTable';
import ProfitAndLossTable from './ProfitAndLossTable';
import ProfitAndLossTableYearly from './ProfitAndLossTableYearly';

type ProfitAndLossTableCompactProps = {
  simulationId?: number;
};

const ProfitAndLossTableCompact: FC<ProfitAndLossTableCompactProps> = ({
  simulationId,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams<Record<string, string>>();
  const resultId = simulationId || params.resultId;
  const labels = [
    t('result.finance.profitAndLoss.toggleButtons.compact'),
    t('result.finance.profitAndLoss.toggleButtons.yearly'),
  ];
  const [selectedIndex, setSelectedIndex] = useState(0);
  const handleViewChange = (index: number) => {
    setSelectedIndex(index);
  };
  const [storageSelection, setStorageSelection] = useState('withStorage');
  const { isLoading, error } = useSWR<SimulationResult>(
    `/simulation/${resultId}/`,
    {
      revalidateOnFocus: false,
    }
  );
  const handleStorageSelection = (value: string) => {
    setStorageSelection(value);
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <Flex justify="center" align="center" style={{ height: 334 }}>
          <Spin style={{ margin: '20px' }} />
        </Flex>
      );
    }

    if (error) {
      return (
        <Flex
          vertical
          gap={12}
          justify="center"
          align="center"
          style={{ height: 334 }}
        >
          <IconError width={50} height={50} fill={theme.colors.tagFailure} />
          <Typography.Text className="fs-14-regular text-gray-color">
            {t('result.error.fetchDataError')}
          </Typography.Text>
        </Flex>
      );
    }

    switch (storageSelection) {
      case 'withStorage':
        return selectedIndex === 0 ? (
          <ProfitAndLossTable simulationId={simulationId} />
        ) : (
          <ProfitAndLossTableYearly />
        );
      case 'withoutStorage':
        return <PNLWithoutStorageTable />;
      case 'storage':
        return <PNLStorageTable />;
      default:
        return null;
    }
  };

  return (
    <Card
      title={
        selectedIndex === 0 ? (
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.profitAndLoss.compact.title')}
            </Typography.Title>
            <Tooltip
              title={t('result.finance.profitAndLoss.compact.title')}
              description={t('result.finance.profitAndLoss.compact.tooltip')}
            >
              <IconInfo />
            </Tooltip>
          </Flex>
        ) : (
          <Flex gap={8} align="center">
            <Typography.Title level={4} className="fs-17-bold">
              {t('result.finance.profitAndLoss.yearly.title')}
            </Typography.Title>
            <Tooltip
              title={t('result.finance.profitAndLoss.yearly.title')}
              description={t('result.finance.profitAndLoss.yearly.tooltip')}
            >
              <IconInfo />
            </Tooltip>
          </Flex>
        )
      }
      $paddingBody="12px 0"
      extra={
        <Flex gap={16} align="center">
          <motion.div
            whileTap={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 300, damping: 24 }}
          >
            <Select
              placeholder={t(
                'result.finance.profitAndLoss.storageSelection.placeholder'
              )}
              style={{ minWidth: '150px', width: '150px' }}
              value={storageSelection}
              onChange={handleStorageSelection}
            >
              <Select.Option value="withStorage">
                {t('result.finance.profitAndLoss.storageSelection.withStorage')}
              </Select.Option>
              <Select.Option value="withoutStorage">
                {t(
                  'result.finance.profitAndLoss.storageSelection.withoutStorage'
                )}
              </Select.Option>
              <Select.Option value="storage">
                {t('result.finance.profitAndLoss.storageSelection.storage')}
              </Select.Option>
            </Select>
          </motion.div>
          {storageSelection === 'withStorage' && (
            <RToggleButtons
              labels={labels}
              selectedIndex={selectedIndex}
              onIndexChange={handleViewChange}
            />
          )}
          <Button icon={<IconDownload fill={theme.colors.bluePrimary} />} />
        </Flex>
      }
      styles={{ header: { padding: '14px 24px' } }}
      style={{ marginTop: '24px' }}
    >
      {renderContent()}
    </Card>
  );
};
export default ProfitAndLossTableCompact;
